/** @jsxImportSource @emotion/react */
import React from 'react';
import { Card, Space, Tag, Typography } from 'antd';

const { Text } = Typography;

interface Props {
  harvestForecast: any;
  isForecastAnnualLike: boolean;
  harvestGrossTargetKg: number | null;
  harvestMarketableTargetKg: number | null;
  plantingQuantity: number;
}

/**
 * На культуре с квантом плода "Доступно к сбору" округлено вниз до целых
 * товарных плодов, поэтому оно стоит на месте по несколько дней подряд, пока
 * растение исправно копит — со стороны это выглядит как замерший счётчик.
 * Считаем остаток сверх целых плодов и оценку срока до следующей ступеньки.
 */
const resolveNextFruitProgress = (harvestForecast: any) => {
  const unitKg = Number(harvestForecast?.fruitUnitWeightKg);
  const ripeKg = Number(harvestForecast?.ripeKg);
  const availableKg = Number(harvestForecast?.availableKg || 0);
  if (!Number.isFinite(unitKg) || unitKg <= 0) return null;
  if (!Number.isFinite(ripeKg) || ripeKg <= 0) return null;

  const remainderKg = ripeKg - availableKg;
  // Хвост меньше грамма — показывать нечего; на сборе целыми плодами остатка нет.
  if (remainderKg <= 0.001) return null;

  const toNextFruitKg = unitKg - remainderKg;
  if (toNextFruitKg <= 0) return null;

  // Оценка по темпу завязи: это скорость поступления массы, а не дозревания,
  // поэтому срок приблизительный и подписан как таковой.
  const dailyKg = Number(harvestForecast?.dailyPotentialKg);
  const etaDays = Number.isFinite(dailyKg) && dailyKg > 0
    ? Math.ceil(toNextFruitKg / dailyKg)
    : null;

  return { remainderKg, toNextFruitKg, etaDays };
};

const HarvestForecastCard: React.FC<Props> = ({
  harvestForecast,
  isForecastAnnualLike,
  harvestGrossTargetKg,
  harvestMarketableTargetKg,
  plantingQuantity,
}) => {
  if (!harvestForecast?.isEnabled && !isForecastAnnualLike) return null;

  const isLeafCut = harvestForecast?.harvestType === 'leaf_cut';
  const population = harvestForecast?.fruitSummary;
  const countBasis = population ? Number(harvestForecast.fruitReferencePlantCount) : plantingQuantity;
  const nextFruit = isLeafCut || population ? null : resolveNextFruitProgress(harvestForecast);

  return (
    <Card size="small" title={population ? "Урожай: состояние плодов" : "Прогноз урожая (симуляция)"}>
      <Space direction="vertical" size={4} css={{ width: '100%' }}>
        {!harvestForecast?.isEnabled && (
          <Text type="secondary">
            Для этой культуры не задана модель урожая в техкарте. Прогноз появится после настройки параметров в разделе "Техкарты".
          </Text>
        )}
        {harvestForecast?.isEnabled && harvestForecast?.isDefaultModel && (
          <Text type="warning">
            Используется базовая модель плодоношения для annual-культуры. Для точной оценки задайте параметры урожая в техкарте.
          </Text>
        )}
        {harvestForecast?.isEnabled && (
          <>
            {isLeafCut && (
              <Text>Срезка зелени: доступно {Math.round(Number(harvestForecast.leafAvailableKg || 0) * 1000)} г/раст.
                {' · '}Отрастание: {Math.round(Number(harvestForecast.leafRegrowthPct || 0))}%
                {' · '}Срезок: {Number(harvestForecast.cutCount || 0)}
                {Number(harvestForecast.daysUntilNextCut) > 0 && ` · До следующей срезки не менее ${harvestForecast.daysUntilNextCut} дн.`}
              </Text>
            )}
            {!population && <Space wrap size={[8, 4]}>
              <Tag color={harvestForecast.ready ? 'green' : (Number(harvestForecast.readinessPct || 0) >= 100 ? 'red' : 'orange')}>
                {harvestForecast.ready
                  ? 'Окно сбора по модели открыто'
                  : (Number(harvestForecast.readinessPct || 0) >= 100
                    ? 'Окно сбора по модели закрыто'
                    : 'Подготовка по модели')}
              </Tag>
              <Text type="secondary">Готовность по модели: {Number(harvestForecast.readinessPct || 0).toFixed(0)}%</Text>
              {!isLeafCut && harvestForecast.peakFruitingEdu != null && (
                <Text type="secondary">Пик плодоношения: ~{Math.round(harvestForecast.peakFruitingEdu)} EDU фазы</Text>
              )}
              {harvestForecast.completeLifecycleOnHarvest && (
                <Text type="warning">Для этой культуры сбор завершает жизненный цикл растения.</Text>
              )}
            </Space>}

            <Text type="secondary">
              {population
                ? 'Масса плодов и готовность к сбору — разные показатели. Готовность зависит от культуры, зрелости и размера плодов. Количество ниже указано по всей посадке.'
                : 'Это фазовая модель урожая из техкарты. Фактическое развитие растения зависит от накопленных единиц развития, температуры, света и общего стресса.'}
            </Text>

            {harvestGrossTargetKg != null && (
              <Text type="secondary">
                Цель техкарты на сезон: валовая <Text strong>{harvestGrossTargetKg.toFixed(2)} кг/раст.</Text>
                {harvestMarketableTargetKg != null && (
                  <>; после товарной доли это около <Text strong>{harvestMarketableTargetKg.toFixed(2)} кг/раст.</Text></>
                )}
              </Text>
            )}

            {population && <Space wrap size={[12, 6]}>
              <Text>Бутоны: <Text strong>{population.budCount}</Text>; цветки: <Text strong>{population.openFlowerCount}</Text></Text>
              {([['green', 'Зелёные'], ['turning', 'Дозревают'], ['ripe', 'Спелые'], ['overripe', 'Перезревшие']] as const).map(([key, label]) => (
                <Text key={key}>{label}: <Text strong>{population.stages[key].count} шт. · {Number(population.stages[key].massKg).toFixed(2)} кг</Text></Text>
              ))}
              <Text>На растениях: <Text strong>{Number(population.totalMassKg).toFixed(2)} кг</Text></Text>
            </Space>}

            <Space wrap size={[12, 6]}>
              <Text>В день: <Text strong>{Number(harvestForecast.dailyPotentialKg || 0).toFixed(3)} кг/раст.</Text></Text>
              <Text>Сформировано всего: <Text strong>{Number(harvestForecast.cumulativePotentialKg || 0).toFixed(2)} кг/раст.</Text></Text>
              <Text>Доступно к сбору: <Text strong>{Number(harvestForecast.availableKg || 0).toFixed(2)} кг/раст.</Text></Text>
              <Text>Уже собрано: <Text strong>{Number(harvestForecast.collectedCumulativeKg || 0).toFixed(2)} кг/раст.</Text></Text>
              <Text>Утрачено: <Text strong>{Number(harvestForecast.lostCumulativeKg || 0).toFixed(2)} кг/раст.</Text></Text>
              <Text>{population ? 'Собрано + на растениях' : 'Прогноз итога'}: <Text strong>{Number(harvestForecast.projectedTotalKg || 0).toFixed(2)} кг/раст.</Text></Text>
            </Space>

            {population && <Text type="secondary">«Собрано + на растениях» учитывает уже произошедшие потери; это текущий баланс массы, а не обещание будущего урожая.</Text>}

            {nextFruit && (
              <Text type="secondary">
                Сбор идёт целыми плодами по {Number(harvestForecast.fruitUnitWeightKg).toFixed(2)} кг, поэтому
                {' '}«доступно» растёт ступеньками. Сверх целых плодов уже налилось{' '}
                <Text strong>{nextFruit.remainderKg.toFixed(2)} кг/раст.</Text>; до следующего плода ещё{' '}
                <Text strong>{nextFruit.toNextFruitKg.toFixed(2)} кг</Text>
                {nextFruit.etaDays != null && <> — это примерно {nextFruit.etaDays} дн. при текущем темпе</>}.
              </Text>
            )}

            {plantingQuantity > 1 && (
              <Space wrap size={[12, 6]}>
                <Text>В день по партии: <Text strong>{(Number(harvestForecast.dailyPotentialKg || 0) * countBasis).toFixed(2)} кг</Text></Text>
                <Text>{population ? 'Собрано + на растениях по партии' : 'Прогноз итога по партии'}: <Text strong>{(Number(harvestForecast.projectedTotalKg || 0) * countBasis).toFixed(2)} кг</Text></Text>
              </Space>
            )}
          </>
        )}
      </Space>
    </Card>
  );
};

export default HarvestForecastCard;
